<template>
  <div class="st">
    <!-- Header -->
    <div class="st__head">
      <div>
        <div class="st__title">Recent Sales</div>
        <div class="st__sub">
          Month: <span class="mono">{{ monthKey }}</span>
          <span class="st__count">{{ rows.length }}</span>
        </div>
      </div>

      <div class="st__tools">
        <label class="st__label">
          <span class="st__labelText">Select month</span>
          <input type="month" class="st__month" v-model="monthKey" @change="load" />
        </label>
      </div>
    </div>

    <!-- Table -->
    <div v-if="rows.length" class="st__tableWrap">
      <table class="st__table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Category</th>
            <th class="num">List</th>
            <th class="num">Sale</th>
            <th class="num">Discount</th>
            <th v-if="admin" class="actions">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="r in rows" :key="r.id">
            <td class="mono">{{ r.dateISO || '—' }}</td>

            <td>
              <span class="pill">{{ r.category || '—' }}</span>
            </td>

            <td class="num mono">₹ {{ fmtMoney(r.listPrice) }}</td>
            <td class="num mono">₹ {{ fmtMoney(r.saleValue) }}</td>

            <td class="num">
              <span
                class="disc"
                :class="Number(r.discountPct || 0) > 0 ? 'disc--on' : 'disc--off'"
              >
                {{ Number(r.discountPct || 0).toFixed(0) }}%
              </span>
            </td>

            <td v-if="admin" class="actions">
              <router-link
                class="btn btn--soft"
                :to="{ name:'admin-sale-edit', params:{ id:r.id } }"
              >
                Edit
              </router-link>
              <button class="btn btn--danger" @click="del(r)">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div v-else class="st__empty">
      <div class="st__emptyTitle">No sales found</div>
      <div class="st__emptySub">Try selecting a different month.</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SalesTable',
  props: { admin: { type: Boolean, default: false } },
  data () {
    return { monthKey: new Date().toISOString().slice(0, 7), rows: [] }
  },
  async created () { await this.load() },
  methods: {
    async load () {
      this.rows = await this.$store.dispatch('sales/fetchMonth', this.monthKey)
    },
    async del (r) {
      if (!confirm('Delete this record?')) return
      await this.$store.dispatch('sales/adminRemoveSale', r.id)
      await this.load()
    },
    fmtMoney (n) {
      try {
        const x = Number(n || 0)
        return x.toLocaleString('en-IN')
      } catch (e) {
        return String(n || 0)
      }
    }
  }
}
</script>

<style scoped>
/* Card shell */
.st{
  background:#fff;
  border:1px solid rgba(0,0,0,.08);
  border-radius:16px;
  box-shadow: 0 10px 30px rgba(0,0,0,.04);
  overflow:hidden;
}

/* Header */
.st__head{
  padding:14px 14px 12px;
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:12px;
  flex-wrap:wrap;
  border-bottom:1px solid rgba(0,0,0,.06);
  background: linear-gradient(180deg, rgba(17,24,39,.03), rgba(255,255,255,0));
}
.st__title{
  font-weight:950;
  font-size:16px;
  letter-spacing:.2px;
}
.st__sub{
  margin-top:4px;
  font-size:12px;
  opacity:.72;
  display:flex;
  gap:10px;
  align-items:center;
  flex-wrap:wrap;
}
.st__count{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width:26px;
  height:20px;
  padding:0 8px;
  border-radius:999px;
  background: rgba(0,0,0,.06);
  font-weight:900;
  opacity:1;
}

.st__tools{ display:flex; gap:10px; align-items:center; }
.st__label{ display:flex; flex-direction:column; gap:6px; }
.st__labelText{ font-size:11px; opacity:.65; font-weight:800; }

.st__month{
  border:1px solid rgba(0,0,0,.12);
  border-radius:12px;
  padding:10px 12px;
  outline:none;
  background:#fff;
  font-weight:800;
}
.st__month:focus{
  border-color: rgba(59,130,246,.55);
  box-shadow: 0 0 0 4px rgba(59,130,246,.12);
}

/* Table */
.st__tableWrap{ overflow:auto; }
.st__table{
  width:100%;
  border-collapse:separate;
  border-spacing:0;
  min-width: 720px; /* makes it nicer on small screens */
}
.st__table thead th{
  position:sticky;
  top:0;
  z-index:1;
  background:#fff;
  border-bottom:1px solid rgba(0,0,0,.08);
  font-size:12px;
  text-transform:uppercase;
  letter-spacing:.06em;
  padding:12px 14px;
  text-align:left;
  opacity:.85;
}
.st__table tbody td{
  padding:12px 14px;
  border-bottom:1px solid rgba(0,0,0,.06);
  vertical-align:middle;
}
.st__table tbody tr:nth-child(odd){ background: rgba(0,0,0,.012); }
.st__table tbody tr:hover{ background: rgba(59,130,246,.05); }

.num{ text-align:right; }
.actions{ text-align:right; white-space:nowrap; }

/* Pills */
.pill{
  display:inline-flex;
  align-items:center;
  padding:5px 10px;
  border-radius:999px;
  border:1px solid rgba(0,0,0,.10);
  background: rgba(0,0,0,.03);
  font-weight:900;
  font-size:12px;
}

/* Discount badge */
.disc{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:4px 10px;
  border-radius:999px;
  font-weight:900;
  font-size:12px;
  border:1px solid rgba(0,0,0,.10);
}
.disc--on{
  background: rgba(16,185,129,.10);
  border-color: rgba(16,185,129,.25);
  color:#065f46;
}
.disc--off{
  background: rgba(156,163,175,.10);
  border-color: rgba(156,163,175,.25);
  color:#374151;
}

/* Buttons */
.btn{
  border:1px solid rgba(0,0,0,.12);
  background:#fff;
  padding:8px 10px;
  border-radius:10px;
  cursor:pointer;
  font-weight:900;
  font-size:12px;
}
.btn--soft{ background: rgba(0,0,0,.03); }
.btn--danger{
  background: rgba(239,68,68,.10);
  border-color: rgba(239,68,68,.25);
  color:#7f1d1d;
  margin-left:8px;
}

/* Empty */
.st__empty{
  padding:18px;
  text-align:center;
}
.st__emptyTitle{ font-weight:950; }
.st__emptySub{ margin-top:6px; opacity:.7; font-size:13px; }

.mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>
